import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchEarnedCommissions, fetchCommissionOpeners } from '../../api';
import DataGrid, { applyPinnedTo } from '../common/DataGrid';
import DateRangePicker from '../common/DateRangePicker';
import { formatMoney, moneyColumn, monthRange, toISODate } from '../marketing/shared';
import { caseColumn, formatPaidAt, formatPct } from './shared';
import '../reports/Reports.css';
import '../marketing/Marketing.css';
import './Commissions.css';

// Earned commission: what each person earned from payments dated in a range
// (the current month by default), as a split agent and as the settlement
// officer on the payment, with every payment behind it. A payment earns only
// what's left under the case's cap after all earlier payments on it, and a
// refund shows as a negative line that claws back.
//
// A payment counts toward one of its case's invoices (or, when it paid one off
// and started the next, part toward each: two lines).
//
// A case nobody has entered on the Splits page has no split agents and no
// cap: its settlement officer earns the usual 20% on every payment. Its lines
// are marked, so staff can see which cases still need entering.

const ROLE_LABEL = { split: 'Split agent', officer: 'Settlement officer' };

// The agent filter's value for the officer share of payments that named no
// settlement officer (the server's agent id 0).
const UNASSIGNED = '0';

// The By Agent grid starts this tall; its corner handle drags it taller or
// shorter.
const AGENT_GRID_HEIGHT = 480;

// sumCents adds up a money field in cents, so totals match the lines to the
// penny.
const sumCents = (rows, field) => rows.reduce((sum, r) => sum + Math.round((r[field] || 0) * 100), 0) / 100;

// The pinned total row under an agent's payments.
const detailTotals = (lines) => (lines.length ? [{
    case_number: 'Total',
    commissionable: sumCents(lines, 'commissionable'),
    commission: sumCents(lines, 'commission'),
}] : []);

export default function Earned() {
    const [range, setRange] = useState(monthRange());
    const [agentId, setAgentId] = useState('');
    const [openers, setOpeners] = useState([]);
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchCommissionOpeners().then((o) => setOpeners(o || [])).catch(() => setOpeners([]));
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setReport(await fetchEarnedCommissions({ ...range, agentId }));
        } catch (err) {
            setError(err.message);
            setReport(null);
        } finally {
            setLoading(false);
        }
    }, [range, agentId]);

    useEffect(() => { load(); }, [load]);

    const totalColumns = useMemo(() => [
        // The expander: each agent opens to the payments behind their total.
        { field: 'agent_name', headerName: 'Agent', flex: 1, minWidth: 200, cellRenderer: 'agGroupCellRenderer' },
        { field: 'cases', headerName: 'Cases', type: 'rightAligned', width: 100 },
        { field: 'lines', headerName: 'Payments', type: 'rightAligned', width: 110 },
        moneyColumn('split_commission', 'As Split Agent'),
        moneyColumn('officer_commission', 'As Settlement Officer'),
        moneyColumn('commission', 'Commission Earned'),
    ], []);

    const lineColumns = useMemo(() => [
        { field: 'paid_at', headerName: 'Paid', width: 150, valueFormatter: (p) => formatPaidAt(p.value) },
        { field: 'agent_name', headerName: 'Agent', minWidth: 160 },
        { field: 'role', headerName: 'Role', width: 150, valueFormatter: (p) => ROLE_LABEL[p.value] || p.value },
        caseColumn(),
        { field: 'client_name', headerName: 'Client', minWidth: 160 },
        {
            // The server sends uncapped (the case isn't entered); the column
            // shows the reverse, as a checkbox: ticked when the case is
            // entered. The formatter is for exports, which write Yes/No.
            colId: 'case_entered',
            headerName: 'Case Entered',
            headerTooltip: 'Unticked means nobody has entered the case on the Splits page: the settlement officer earns on every payment, with no cap and no split agents',
            width: 130,
            cellDataType: 'boolean',
            valueGetter: (p) => (p.data && !p.node.rowPinned ? !p.data.uncapped : null),
            valueFormatter: (p) => (p.value == null ? '' : p.value ? 'Yes' : 'No'),
        },
        {
            colId: 'invoice',
            headerName: 'Invoice',
            headerTooltip: 'The case\'s invoice the payment counted toward',
            minWidth: 150,
            valueGetter: (p) => {
                if (!p.data || p.node.rowPinned || !p.data.invoice_position) return '';
                return `Invoice ${p.data.invoice_position}${p.data.invoice_label ? ` · ${p.data.invoice_label}` : ''}`;
            },
        },
        { field: 'split_pct', headerName: 'Share', headerTooltip: 'Their percentage of the payment', type: 'rightAligned', width: 90, valueFormatter: (p) => formatPct(p.value) },
        { field: 'kind', headerName: 'Kind', width: 100 },
        { ...moneyColumn('amount', 'Payment'), headerTooltip: 'The part of the payment that went to this invoice: all of it, unless it paid off one invoice and started the next' },
        { ...moneyColumn('counted', 'Counted'), headerTooltip: 'How much of the payment counted toward the case\'s commissionable amount' },
        { ...moneyColumn('commissionable', 'Commissionable'), headerTooltip: 'Their share of what counted' },
        { field: 'commission_pct', headerName: 'Rate', type: 'rightAligned', width: 90, valueFormatter: (p) => formatPct(p.value) },
        moneyColumn('commission', 'Commission'),
    ], []);

    const defaultColDef = useMemo(() => ({ sortable: true, filter: true, resizable: true, minWidth: 90 }), []);

    // Each agent's lines, for their drilldown.
    const linesByAgent = useMemo(() => {
        const byAgent = new Map();
        (report ? report.lines : []).forEach((l) => {
            if (!byAgent.has(l.agent_id)) byAgent.set(l.agent_id, []);
            byAgent.get(l.agent_id).push(l);
        });
        return byAgent;
    }, [report]);

    // The By Agent drilldown: expanding an agent shows the payments that make
    // up their commission (the Payments grid's columns, less the agent), with
    // a total that follows the sub-table's own filters.
    const detailProps = useMemo(() => {
        const detailColumns = lineColumns
            .filter((c) => c.field !== 'agent_name')
            // The case column carries the total row's label.
            .map((c) => (c.field === 'case_number' ? { ...c, cellDataType: false } : c));
        return {
            masterDetail: true,
            // Size the expanded panel to its payments, capped so an agent with
            // hundreds scrolls inside it instead of stretching the grid.
            getRowHeight: (p) => {
                if (!p.node.detail) return undefined;
                const lines = linesByAgent.get(p.node.parent?.data?.agent_id) || [];
                return Math.min(128 + (lines.length + 1) * 42, 520);
            },
            detailCellRendererParams: (masterParams) => {
                const lines = linesByAgent.get(masterParams.data.agent_id) || [];
                return {
                    detailGridOptions: {
                        columnDefs: detailColumns,
                        defaultColDef: { sortable: true, filter: true, resizable: true, minWidth: 90, flex: 1 },
                        cellSelection: true,
                        statusBar: { statusPanels: [{ statusPanel: 'agAggregationComponent', align: 'right' }] },
                        pinnedBottomRowData: detailTotals(lines),
                        onModelUpdated: (e) => {
                            const visible = [];
                            e.api.forEachNodeAfterFilter((n) => { if (n.data) visible.push(n.data); });
                            applyPinnedTo(e.api, detailTotals(visible));
                        },
                    },
                    getDetailRowData: (p) => p.successCallback(lines),
                };
            },
        };
    }, [lineColumns, linesByAgent]);

    const grandTotal = useMemo(
        () => (report ? report.totals.reduce((sum, t) => sum + Math.round(t.commission * 100), 0) / 100 : 0),
        [report],
    );

    const splitTotal = useMemo(
        () => (report ? report.totals.reduce((sum, t) => sum + Math.round(t.split_commission * 100), 0) / 100 : 0),
        [report],
    );
    const uncappedCases = useMemo(
        () => (report ? new Set(report.lines.filter((l) => l.uncapped).map((l) => l.case_number)).size : 0),
        [report],
    );

    const fileStem = () => `Commissions_${range.startDate}_to_${range.endDate}`;

    return (
        <div className="page">
            <div className="mkt__header">
                <div>
                    <h1 className="page__title">Earned Commissions</h1>
                    <p className="page__subtitle">
                        Commission earned from client payments dated in the range, by split agents and by the
                        settlement officer on each payment.
                    </p>
                </div>
            </div>

            <div className="mkt__controls">
                <div className="mkt__field">
                    <span>Payments dated</span>
                    <DateRangePicker value={range} onChange={setRange} />
                </div>
                <label className="mkt__field">
                    <span>Agent</span>
                    <select value={agentId} onChange={(e) => setAgentId(e.target.value)}>
                        <option value="">Everyone</option>
                        {openers.map((o) => (
                            <option key={o.id} value={o.id}>{o.name}{o.active ? '' : ' (inactive)'}</option>
                        ))}
                        <option value={UNASSIGNED}>(no settlement officer on the payment)</option>
                    </select>
                </label>
            </div>

            {error && <p className="mkt__error">{error}</p>}
            {loading && <p className="page__subtitle">Calculating…</p>}

            {report && !loading && (
                <>
                    <div className="com__stats">
                        <div className="com__stat">
                            <div className="com__stat-label">Total earned</div>
                            <div className="com__stat-value">{formatMoney(grandTotal)}</div>
                            <div className="com__stat-sub">{report.totals.length} agent{report.totals.length === 1 ? '' : 's'}</div>
                        </div>
                        <div className="com__stat">
                            <div className="com__stat-label">Split agents</div>
                            <div className="com__stat-value">{formatMoney(splitTotal)}</div>
                        </div>
                        <div className="com__stat">
                            <div className="com__stat-label">Settlement officers</div>
                            <div className="com__stat-value">{formatMoney(grandTotal - splitTotal)}</div>
                            {uncappedCases > 0 && (
                                <div className="com__stat-sub">
                                    {uncappedCases} case{uncappedCases === 1 ? '' : 's'} not entered yet (no cap)
                                </div>
                            )}
                        </div>
                    </div>

                    {report.lines.length === 0 ? (
                        <p className="page__subtitle">No commission was earned in this range.</p>
                    ) : (
                        <>
                            <h2 className="mkt__panel-title">By agent</h2>
                            <p className="mkt__panel-hint">
                                Expand an agent to see the payments behind their commission. Drag the bottom-right
                                corner to make the table taller or shorter.
                            </p>
                            <div className="com__resizable" style={{ height: AGENT_GRID_HEIGHT }}>
                                <DataGrid
                                    rowData={report.totals}
                                    columnDefs={totalColumns}
                                    defaultColDef={defaultColDef}
                                    height="100%"
                                    enableExport
                                    exportFileName={() => `${fileStem()}_totals`}
                                    getRowId={(p) => String(p.data.agent_id)}
                                    {...detailProps}
                                />
                            </div>

                            <h2 className="mkt__panel-title" style={{ marginTop: 24 }}>Payments</h2>
                            <DataGrid
                                rowData={report.lines}
                                columnDefs={lineColumns}
                                defaultColDef={defaultColDef}
                                height="calc(100vh - 300px)"
                                enableExport
                                exportFileName={() => `${fileStem()}_payments_${toISODate(new Date())}`}
                            />
                        </>
                    )}
                </>
            )}
        </div>
    );
}
